<template>
  <div class="container-fluid">
    <confirm-dialog ref="confirmDialog"></confirm-dialog>

    <SpellStaticSidePanel
      v-if="isSidePanelOpen"
      :spell="staticSpell"
      :spells="spells"
      :runes="runes"
      @add-static-spell="addStaticSpellHarness"
      @update-static-spell="updateStaticSpellHarness"
      @delete-static-spell="deleteStaticSpellHarness"
      @close-static-spell-panel="handleSpellStaticPanelClose"
    />

    <notifications
      width="500"
      group="bb-confirm"
      :speed="100"
      :duration="1000"
      position="top right"
      classes="vue-notification"
    />

    <div class="row">
      <div class="col-md-1 rounded">
        <SpellStaticButtonsPanel
          @show-spell-static-panel="showSpellStaticPanel"
        />
      </div>

      <div class="col-md-11">
        <TableSpellsStatic
          ref="TableSpellsStatic"
          v-if="spells && loadReady"
          :headings="headings"
          :spells="spells"
          @show-spell-static-panel="handleShowSpellStaticPanel"
          @delete-static-spell="deleteStaticSpellHarness"
        />
      </div>
    </div>

    <div
      v-if="!loadReady"
      class="bb-loader"
    ></div>
  </div>
</template>

<script>
import { inject } from 'vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import TableSpellsStatic from '../components/TableSpellsStatic.vue'
import SpellStaticSidePanel from '../components/SpellStaticSidePanel.vue'
import SpellStaticButtonsPanel from '../components/SpellStaticButtonsPanel.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'

import useEditStaticSpell from '../composables/useEditStaticSpell'
import showNotification from '../helpers/showNotification'
import handleError from '../helpers/handleError'

export default {
  name: 'SpellsStaticView',
  setup() {
    const global = inject('global')
    return { global }
  },
  components: {
    TableSpellsStatic,
    SpellStaticSidePanel,
    SpellStaticButtonsPanel,
    ConfirmDialog,
  },
  data() {
    return {
      isSidePanelOpen: false,
      loadReady: false,
      staticSpell: null,
      spells: [],
      runes: [],
      headings: [
        {
          id: 0,
          label: '#',
          field: 'rowNumber',
          filterable: false,
          sortable: true,
          tdClass: 'text-center',
          width: '4em',
        },
        {
          id: 5,
          label: 'Spell',
          field: 'name',
          width: '14em',
          filterable: false,
          sortable: true,
          formatFn: (value, row) => row.display_name || row.name,
        },
        {
          id: 7,
          label: 'Type',
          field: 'type',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
        },
        {
          id: 10,
          label: 'Description',
          field: 'short_description',
          filterable: false,
          sortable: false,
          width: '22em',
        },
        {
          id: 15,
          label: '',
          field: 'fill0',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 35,
          label: 'Progressive',
          field: 'progressive_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 20,
          label: 'Triggered',
          field: 'triggered_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          width: '6em',
        },
        {
          id: 40,
          label: '',
          field: 'fill1',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 45,
          label: 'Mag',
          field: 'mag',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: true,
        },
        {
          id: 50,
          label: 'Resist',
          field: 'resist',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          formatFn: (value) => (value === 'n/a' ? '' : value),
        },
        {
          id: 55,
          label: 'Area',
          field: 'area',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          formatFn: (value) => (value === 'n/a' ? '' : value),
        },
        {
          id: 65,
          label: 'Range',
          field: 'range',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 70,
          label: 'Duration',
          field: 'duration',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 75,
          label: 'Casting Time',
          field: 'casting_time',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 80,
          label: 'Casting Rune',
          field: 'casting_rune_name',
          width: '10em',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
        },
        {
          id: 85,
          label: 'Secondary Rune',
          field: 'secondary_rune_name',
          width: '10em',
          tdClass: 'text-center',
          filterable: true,
          sortable: true,
        },
        {
          id: 90,
          label: 'Cults',
          field: 'cults_display',
          width: '10em',
          tdClass: 'text-left',
          filterable: true,
          sortable: true,
        },
        {
          id: 105,
          label: 'Id',
          field: 'id',
          hidden: true,
        },
        {
          id: 112,
          label: 'Progressive Flag',
          field: 'progressive',
          hidden: true,
        },
        {
          id: 115,
          label: 'Triggered Flag',
          field: 'triggered',
          hidden: true,
        },
      ],
    }
  },
  computed: {
    ...mapGetters('spells', {
      getSpells: 'getSpells',
    }),
    ...mapGetters('characters', {
      getRunes: 'getRunes',
    }),
  },
  methods: {
    ...mapMutations('auth', {
      setCurrentView: 'setCurrentView',
    }),
    ...mapActions('spells', {
      loadStaticSpells: 'loadStaticSpells',
    }),
    ...mapActions('characters', {
      loadStaticRunes: 'loadStaticRunes',
    }),
    normaliseRows(rows = []) {
      return [...(Array.isArray(rows) ? rows : [])]
        .sort((left, right) =>
          (left.name || '').localeCompare(right.name || ''),
        )
        .map((spell, index) => ({
          ...spell,
          cults_display: this.formatCults(spell),
          originalIndex: index,
        }))
    },
    formatCults(spell) {
      return [spell?.cult1, spell?.cult2]
        .filter((cult) => cult && cult !== 'n/a')
        .join(', ')
    },
    setView(view) {
      this.setCurrentView(view)
    },
    async loadStaticSpellData() {
      this.loadReady = false

      try {
        await Promise.all([this.loadStaticSpells(true), this.loadStaticRunes()])
        this.spells = this.normaliseRows(this.getSpells)
        this.runes = this.getRunes || []
        this.loadReady = true
      } catch (err) {
        this.loadReady = true
        handleError(err, 42)
      }
    },
    showSpellStaticPanel() {
      this.staticSpell = null
      this.isSidePanelOpen = true
    },
    handleShowSpellStaticPanel(spell) {
      this.staticSpell = spell ?? null
      this.isSidePanelOpen = true
    },
    handleSpellStaticPanelClose() {
      this.isSidePanelOpen = false
    },
    upsertSpellRow(spell) {
      if (!spell?.id) {
        return
      }

      const index = this.spells.findIndex((row) => row.id === spell.id)
      if (index === -1) {
        this.spells = this.normaliseRows([...this.spells, spell])
        return
      }

      const nextSpells = [...this.spells]
      nextSpells.splice(index, 1, { ...nextSpells[index], ...spell })
      this.spells = this.normaliseRows(nextSpells)
    },
    formatSpellActionMessage(spell, action) {
      return `${spell?.name || 'Spell'} ${action}.`
    },
    async addStaticSpellHarness(newSpell) {
      try {
        const savedSpell = await useEditStaticSpell({
          action: 'ADD',
          payload: newSpell.payload,
        })

        this.upsertSpellRow(savedSpell)
        await this.loadStaticSpellData()
        showNotification(this.formatSpellActionMessage(savedSpell, 'added'))
      } catch (err) {
        handleError(err, 38)
      }
    },
    async updateStaticSpellHarness(updatedSpell) {
      if (!updatedSpell?.id) {
        console.warn('Static spell update skipped: no id found', updatedSpell)
        return
      }

      try {
        const savedSpell = await useEditStaticSpell({
          action: 'UPDATE',
          id: updatedSpell.id,
          payload: updatedSpell.payload,
        })

        this.upsertSpellRow(savedSpell)
        await this.loadStaticSpellData()
        showNotification(this.formatSpellActionMessage(savedSpell, 'updated'))
      } catch (err) {
        handleError(err, 47)
      }
    },
    async deleteStaticSpellHarness(spell) {
      if (!spell?.id) {
        return
      }

      const ok = await this.confirmStaticSpellDeletion(spell)
      if (!ok) {
        return
      }

      try {
        await useEditStaticSpell({
          action: 'DELETE',
          id: spell.id,
        })

        this.spells = this.spells.filter((row) => row.id !== spell.id)
        this.staticSpell = null
        this.isSidePanelOpen = false
        showNotification(this.formatSpellActionMessage(spell, 'deleted'))
      } catch (err) {
        handleError(err, 40)
      }
    },
    confirmStaticSpellDeletion(spell) {
      const characterSpellCount = Number(spell?.character_spell_count ?? 0)
      const message = characterSpellCount
        ? `${characterSpellCount} Characters currently know this spell.`
        : 'Confirm Deletion'

      return this.$refs.confirmDialog.show({
        title: 'Delete Static Spell',
        message,
        subject: spell.name,
        okButton: 'Delete',
        cancelButton: 'Cancel',
      })
    },
    exitView() {
      this.setCurrentView('')
      this.$router.go(-1)
    },
  },
  mounted() {
    //-----------------
    // set spells view
    //-----------------
    this.setView('spells')
    this.loadStaticSpellData()
    this.emitter.on('exit-spell-static-view', this.exitView)
  },
  beforeUnmount() {
    this.emitter.off('exit-spell-static-view', this.exitView)
  },
}
</script>
